import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiCheckAll, mdiCog } from '@lumapps/lumx/icons';
import { Emphasis, FlexBox, IconButton, Orientation } from '@lumapps/lumx/react';
import { Link } from '@lumapps/router';
import { useTranslate } from '@lumapps/translations';
import { getUserSettingsRoute, USER_SETTINGS_VIEWS } from '@lumapps/user-settings/routes';

import { DATA_NAMESPACE } from '../../constants';
import { NOTIFICATIONS_CENTER } from '../../keys';
import { NotificationStatus } from '../../types';
import { NotificationListHeaderContextMenu } from './NotificationListHeaderContextMenu';

export interface NotificationListHeaderActionsProps {
    /** whether the new notifications UI is enabled */
    isNotificationsEnabled: boolean;
    /** current notification filter status */
    notificationStatus: NotificationStatus;
    /** count of unread notifications */
    unreadNotificationsCount: number;
    /** callback to mark all notifications as read */
    setAllAsRead: () => void;
    /** callback to delete all notifications (will trigger confirmation) */
    onDeleteAll: () => void;
    /** callback to navigate to settings */
    onSettingsClick: (evt?: React.MouseEvent) => void;
}

/**
 * Action buttons for the notification list header.
 * Handles different layouts for mobile/desktop and different filter states.
 */
export const NotificationListHeaderActions: React.FC<NotificationListHeaderActionsProps> = ({
    isNotificationsEnabled,
    notificationStatus,
    unreadNotificationsCount,
    setAllAsRead,
    onDeleteAll,
    onSettingsClick,
}) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(DATA_NAMESPACE);

    // Render context menu with appropriate items
    const renderContextMenu = (showMarkAllAsRead?: boolean, showDeleteAll?: boolean) => (
        <NotificationListHeaderContextMenu
            onDeleteAll={onDeleteAll}
            onSetAllAsRead={setAllAsRead}
            onSettingsClick={onSettingsClick}
            showMarkAllAsRead={showMarkAllAsRead}
            showDeleteAll={showDeleteAll}
        />
    );

    // Legacy mode: always show context menu with all options
    if (!isNotificationsEnabled) {
        return renderContextMenu();
    }

    const isUnreadFilter = notificationStatus === NotificationStatus.unread;

    // When FF enabled: always show icon-only button to prevent overflow with long translations
    return (
        <FlexBox orientation={Orientation.horizontal}>
            <IconButton
                icon={mdiCheckAll}
                onClick={setAllAsRead}
                isDisabled={unreadNotificationsCount === 0}
                emphasis={Emphasis.low}
                label={translateKey(NOTIFICATIONS_CENTER.MARK_ALL_AS_READ)}
                {...get({ element: 'mark-all-as-read' })}
            />
            {isUnreadFilter ? (
                <IconButton
                    emphasis={Emphasis.low}
                    icon={mdiCog}
                    onClick={onSettingsClick}
                    {...get({ element: 'settings' })}
                    label={translateKey(NOTIFICATIONS_CENTER.NOTIFICATIONS_SETTINGS)}
                    linkAs={Link}
                    to={getUserSettingsRoute(USER_SETTINGS_VIEWS.NOTIFICATIONS)}
                />
            ) : (
                renderContextMenu(false, true) // show context menu (notification settings, divider, delete all)
            )}
        </FlexBox>
    );
};
